/**
 * The phone type scale (ROADMAP "The phone branch" (j), 2026-09-29).
 *
 * Measured at 412px before this: page titles rendered at desktop display sizes
 * (h4 32px, the landing h1 35px, h2 28px) while BODY text was already small
 * (median 11-12px). So the space goes back from the headings, not the body —
 * shrinking body text further would cost the reading the phone is for.
 *
 * And one floor: form inputs are 16px below `md`. iOS Safari zooms the whole
 * page when a smaller input takes focus; the Research search and the chat box
 * were 13-15px, so tapping either zoomed the page.
 *
 * Applied once, to both themes, below the same `md` line the phone shell uses.
 */
import type { Theme } from '@mui/material/styles';

/** Heading sizes on a phone, in px. Desktop sizes are untouched. */
export const PHONE_HEADING_PX = { h1: 26, h2: 22, h3: 20, h4: 22, h5: 18, h6: 16 } as const;

/** iOS zooms on focus below this. */
export const PHONE_INPUT_MIN_PX = 16;

export function withPhoneTypeScale(theme: Theme): Theme {
  const phone = theme.breakpoints.down('md');
  (Object.keys(PHONE_HEADING_PX) as Array<keyof typeof PHONE_HEADING_PX>).forEach((variant) => {
    const current = theme.typography[variant] as Record<string, unknown>;
    theme.typography[variant] = {
      ...current,
      [phone]: { fontSize: `${PHONE_HEADING_PX[variant]}px` },
    } as typeof theme.typography[typeof variant];
  });

  // The themes set no MuiInputBase override today; keep any that appears.
  const existing = theme.components?.MuiInputBase?.styleOverrides?.input;
  const base = existing && typeof existing === 'object' ? existing : {};
  theme.components = {
    ...theme.components,
    MuiInputBase: {
      ...theme.components?.MuiInputBase,
      styleOverrides: {
        ...theme.components?.MuiInputBase?.styleOverrides,
        input: { ...base, [phone]: { fontSize: `${PHONE_INPUT_MIN_PX}px` } },
      },
    },
  };
  return theme;
}
